export interface ColumnOverride {
  label?: string;
  format?: (value: unknown) => string;
  cell?: 'image' | 'boolean-badge' | 'side-badge' | 'link' | 'pnl';
  /**
   * Build an external URL for this cell. Receives the cell value and the full row so links can
   * be built from a sibling field (e.g. the question cell links via `row.market_slug`).
   */
  link?: (value: unknown, row: Record<string, unknown>) => string;
  foreignKey?: {
    table: string;
    displayColumn: string;
    filterColumn: string;
  };
  /**
   * Marks this column as "virtual": it does not exist on the current table's Prisma model but is
   * reachable via a relation. The backend flattens `<relation>.<field>` onto the row and rewrites
   * filters/sorts to go through the relation. Example: on `trades`, `agent_id` is virtual via
   * `order.agent_id`.
   */
  via?: { relation: string; field: string };
  hidden?: boolean;
}

export interface TableOverride {
  columns?: Record<string, ColumnOverride>;
  defaultSort?: { id: string; desc: boolean };
  hiddenColumns?: string[];
}

function truncateHash(hash: unknown): string {
  const s = String(hash);
  if (s.length <= 16) {
    return s;
  }
  return `${s.slice(0, 8)}...${s.slice(-6)}`;
}

export const tableOverrides: Record<string, TableOverride> = {
  trades: {
    columns: {
      agent_id: {
        label: 'Agent',
        foreignKey: { table: 'agents', displayColumn: 'name', filterColumn: 'id' },
        via: { relation: 'order', field: 'agent_id' }
      },
      question: {
        label: 'Question',
        via: { relation: 'order', field: 'question' },
        link: (_val, row) => {
          const slug = row.market_slug;
          return typeof slug === 'string' && slug.length > 0 ? `https://polymarket.com/market/${slug}` : '';
        }
      },
      outcome: {
        label: 'Outcome',
        via: { relation: 'order', field: 'outcome' }
      },
      side: {
        label: 'Side',
        via: { relation: 'order', field: 'side' },
        cell: 'side-badge'
      },
      // Pulled in via the order relation so the question cell can build its Polymarket URL.
      // Hidden from the table — the data is still flattened onto each row by the backend.
      market_slug: {
        via: { relation: 'order', field: 'market_slug' },
        hidden: true
      },
      fee_rate_bps: {
        label: 'Fee (bps)',
        format: val => String(val)
      },
      transaction_hash: {
        label: 'Tx Hash',
        format: val => truncateHash(val),
        link: val => `https://polygonscan.com/tx/${val}`
      },
      price: {
        format: val => `$${Number(val).toFixed(4)}`
      },
      size: {
        format: val => Number(val).toFixed(4)
      },
      cost_basis: {
        label: 'Cost Basis',
        format: val => `$${Number(val).toFixed(4)}`
      },
      unrealized_gains: {
        label: 'Unrealized P&L',
        format: val => `$${Number(val).toFixed(4)}`
      },
      realized_gains: {
        label: 'Realized P&L',
        format: val => `$${Number(val).toFixed(4)}`
      },
      resolution_price: {
        label: 'Resolution',
        format: val => (val != null ? `$${Number(val).toFixed(4)}` : '—')
      },
      order_id: {
        label: 'Order',
        foreignKey: { table: 'orders', displayColumn: 'id', filterColumn: 'id' }
      },
      resolved: { cell: 'boolean-badge' },
      trader_side: { cell: 'side-badge', label: 'Role' },
      status: { cell: 'side-badge' },
      simulated: {
        label: 'Simulated',
        via: { relation: 'order', field: 'simulated' },
        cell: 'boolean-badge'
      }
    },
    defaultSort: { id: 'id', desc: true }
  },
  orders: {
    columns: {
      agent_id: {
        label: 'Agent',
        foreignKey: { table: 'agents', displayColumn: 'name', filterColumn: 'id' }
      },
      question: {
        label: 'Question',
        link: (_val, row) => {
          const slug = row.market_slug;
          return typeof slug === 'string' && slug.length > 0 ? `https://polymarket.com/market/${slug}` : '';
        }
      },
      market_slug: { hidden: true },
      side: { cell: 'side-badge' },
      size: {
        format: val => Number(val).toFixed(4)
      },
      price: {
        format: val => `$${Number(val).toFixed(4)}`
      },
      polymarket_order_id: {
        label: 'Polymarket ID',
        format: val => truncateHash(val)
      },
      status: { cell: 'side-badge' },
      order_type: { label: 'Type' },
      simulated: { label: 'Simulated', cell: 'boolean-badge' }
    },
    defaultSort: { id: 'id', desc: true }
  },
  llm_usage: {
    columns: {
      agent_id: {
        label: 'Agent',
        foreignKey: { table: 'agents', displayColumn: 'name', filterColumn: 'id' }
      },
      cost_usd: {
        label: 'Cost (USD)',
        format: val => `$${Number(val).toFixed(6)}`
      },
      input_tokens: {
        label: 'Input Tokens',
        format: val => Number(val).toLocaleString()
      },
      output_tokens: {
        label: 'Output Tokens',
        format: val => Number(val).toLocaleString()
      }
    },
    defaultSort: { id: 'id', desc: true }
  },
  agents: {
    columns: {
      imageUrl: { cell: 'image', label: 'Image' },
      active: { cell: 'boolean-badge' }
    },
    defaultSort: { id: 'id', desc: true }
  },
  rewards: {
    columns: {
      amount_usd: {
        label: 'Amount (USD)',
        format: val => `$${Number(val).toFixed(6)}`
      },
      reward_type: { label: 'Type', cell: 'side-badge' },
      transaction_hash: {
        label: 'Tx Hash',
        format: val => truncateHash(val),
        link: val => `https://polygonscan.com/tx/${val}`
      },
      condition_id: {
        label: 'Market',
        format: val => (val ? truncateHash(val) : '—')
      },
      raw_json: {
        label: 'Raw',
        format: val => JSON.stringify(val)
      }
    },
    defaultSort: { id: 'awarded_at', desc: true }
  },
  agents_performance_history: {
    columns: {
      agent_id: {
        label: 'Agent',
        foreignKey: { table: 'agents', displayColumn: 'name', filterColumn: 'id' }
      },
      profit_loss: {
        label: 'P&L',
        cell: 'pnl',
        format: val => `$${Number(val).toFixed(2)}`
      },
      positions_value: {
        label: 'Positions Value',
        format: val => `$${Number(val).toFixed(2)}`
      },
      realized_profit_loss: {
        label: 'Realized P&L',
        cell: 'pnl',
        format: val => `$${Number(val).toFixed(2)}`
      },
      total_fees_paid: {
        label: 'Total Fees',
        format: val => `$${Number(val).toFixed(2)}`
      },
      sim_trade_count: { label: 'Sim Trades' },
      sim_pnl: {
        label: 'Sim P&L',
        cell: 'pnl',
        format: val => `$${Number(val).toFixed(2)}`
      },
      number_of_trades: { label: 'Trades' },
      number_of_buy_trades: { label: 'Buy Trades' },
      number_of_sell_trades: { label: 'Sell Trades' },
      realized_won_trades: { label: 'Won' },
      realized_lost_trades: { label: 'Lost' },
      realized_split_trades: { label: 'Split' }
    },
    defaultSort: { id: 'id', desc: true }
  }
};
